import { blinkConstants } from '@thoughtspot/blink-constants';
import { FISCAL_CALENDAR_NAME, getCustomCalendarGuid as getCalendarGuid, getDisplayString, GREGORIAN_CALENDAR_NAME, } from '@thoughtspot/custom-calendar-service';
import { dateFormatPresets, formatDate, formatDateNum, formatTimeOfDay, } from '@thoughtspot/date-format-util';
import { getDefaultCurrencyCode, getValueFormattedByConfig, } from '@thoughtspot/number-format-util';
import { enableInferCurrencyFromBrowserAtViz, includeMilliSeconds, isDefaultNumberFormatUnitNone, } from '@thoughtspot/session-service';
import { convertValueFromBackend, getCustomCalendarGuidFromColumn, getCustomCalendarValueFromEpoch, getEffectiveDateNumDataType, hasCustomCalendar, isAttribute, isColumnValidForNumberFormatting, isDateColumn, isDateNumColumn, isDateTimeColumn, isDoubleNumberAggregation, isEffectivelyNumeric, isNumeric, isTimeColumn, isWholeNumberAggregation, showDateFinancialYearFormat, } from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import { getColumnCurrencyInfo, getFormatPattern, getFormatPatternDecimalCount, getLocale, getUniqueCurrencyCode, isColumnCurrencyFormatType, isCurrencyColumn, isPercentColumn, } from './column-format-util';
import { genericFormatter } from './formatter/formatter-util';
import { mkFloatingPointFormatter, mkNumericFormatter, } from './formatter/generic-value-formatters';
import { CategoryType, CurrencyFormatType, NegativeValueFormat, Unit, } from './types/prism';
import { singleObjectParamMemoize } from './utils/cache';
export const DEFAULT_HIGHCHARTS_CSS_UNIT = 'px';
export const decimalsForFormat = (format, unit) => {
    let decimals;
    if (unit === Unit.Auto) {
        return 2;
    }
    if (format.category === CategoryType.Number) {
        decimals = format.numberFormatConfig.decimals;
    }
    else if (format.category === CategoryType.Currency) {
        decimals = format.currencyFormatConfig.decimals;
    }
    else if (format.category === CategoryType.Percentage) {
        decimals = format.percentageFormatConfig.decimals + 2;
    }
    else if (format.category === CategoryType.Custom &&
        !_.isEmpty(format.customFormatConfig.format)) {
        const subPatterns = format.customFormatConfig.format?.split(';');
        decimals = 0;
        subPatterns.forEach(subPattern => {
            const splitFormat = format.customFormatConfig.format.split('.');
            decimals +=
                splitFormat.length > 1 && !_.isEmpty(splitFormat[1])
                    ? splitFormat[1].match(/(0|#)/g)?.length
                    : 0;
        });
    }
    else {
        decimals = 0;
    }
    return decimals;
};
function getBaseTypeFormatterInstanceExpensive(col) {
    let formatPattern = getFormatPattern(col);
    if (isEffectivelyNumeric(col) ||
        (isNumeric(col) && col.format?.currencyFormat)) {
        return mkNumericFormatter(col);
    }
    if (isDateColumn(col)) {
        const showFinancialFormat = showDateFinancialYearFormat(col);
        const isDateTime = isDateTimeColumn(col);
        if (!formatPattern) {
            if (isDateTime) {
                formatPattern = includeMilliSeconds()
                    ? dateFormatPresets.DATETIME_SHORT_WITH_MILLIS
                    : dateFormatPresets.DATETIME_SHORT_WITH_SECONDS;
            }
            else {
                formatPattern = dateFormatPresets.DATE_SHORT;
            }
        }
        return (dataValue, options) => {
            const customCalendarValueFromEpoch = getCustomCalendarValueFromEpoch(col, dataValue, options?.displayToCustomCalendarValueMap);
            const customCalendarDisplayStr = getDisplayString(customCalendarValueFromEpoch);
            if (customCalendarValueFromEpoch && customCalendarDisplayStr) {
                return customCalendarDisplayStr;
            }
            const customCalendarOverridesFiscalOffset = hasCustomCalendar(col) &&
                getCustomCalendarGuidFromColumn(col) !==
                    getCalendarGuid(FISCAL_CALENDAR_NAME);
            const isGregorianCalendar = hasCustomCalendar(col) &&
                getCustomCalendarGuidFromColumn(col) ===
                    getCalendarGuid(GREGORIAN_CALENDAR_NAME);
            options = _.merge(options, {
                isGregorianCalendar,
                customCalendarOverridesFiscalOffset: !!customCalendarOverridesFiscalOffset,
            });
            return formatDate(dataValue, formatPattern, !showFinancialFormat, options);
        };
    }
    if (isDateNumColumn(col)) {
        return (dataValue, options) => {
            const customCalendarValueFromEpoch = getCustomCalendarValueFromEpoch(col, dataValue, options?.displayToCustomCalendarValueMap);
            const customCalendarDisplayStr = getDisplayString(customCalendarValueFromEpoch);
            if (customCalendarValueFromEpoch && customCalendarDisplayStr) {
                return customCalendarDisplayStr;
            }
            const isGregorianCalendar = hasCustomCalendar(col) &&
                getCustomCalendarGuidFromColumn(col) ===
                    getCalendarGuid(GREGORIAN_CALENDAR_NAME);
            const formatOptions = {
                ...options,
                isGregorianCalendar,
            };
            return formatDateNum(getEffectiveDateNumDataType(col), dataValue, formatPattern, formatOptions);
        };
    }
    if (isTimeColumn(col)) {
        formatPattern = formatPattern || 'HH:mm:ss';
        return dataValue => formatTimeOfDay(dataValue, formatPattern);
    }
    return genericFormatter;
}
const getBaseTypeFormatterInstance = singleObjectParamMemoize(getBaseTypeFormatterInstanceExpensive);
export const getDataFormatter = (col, aggrTypeOverride) => {
    if (isWholeNumberAggregation(aggrTypeOverride)) {
        return mkNumericFormatter(col);
    }
    if (isDoubleNumberAggregation(aggrTypeOverride)) {
        return mkFloatingPointFormatter(col);
    }
    return getBaseTypeFormatterInstance(col);
};
export const getFormattedValue = (col, rawValue, isAlreadyConvertedFromBackend = false, params = {
    noShorten: true,
}, escapeHTML = false) => {
    let value = rawValue;
    if (!isAlreadyConvertedFromBackend) {
        value = convertValueFromBackend(col, rawValue);
    }
    if (isAttribute(col) && typeof value === 'string') {
        value = value.replace(/[\x1A]/g, '');
    }
    const dataFormatter = getDataFormatter(col);
    const formattedVal = `${dataFormatter(value, {
        ...params,
        displayToCustomCalendarValueMap: params.displayToCustomCalendarValueMap || {
            [value]: rawValue,
        },
    })}`;
    return escapeHTML ? _.escape(formattedVal) : formattedVal;
};
export const getNumericColumnFormatPattern = (col, allowCustomFormatPattern = true) => {
    const defaultBusinessFormatDecimalCount = 2;
    const decimalCount = getFormatPatternDecimalCount(col);
    let formatConfig = {
        __typename: 'FormatConfig',
        isCategoryEditable: true,
    };
    const formatPattern = allowCustomFormatPattern
        ? getFormatPattern(col)
        : undefined;
    if (formatPattern || isColumnCurrencyFormatType(col)) {
        formatConfig = {
            ...formatConfig,
            category: CategoryType.Custom,
            customFormatConfig: {
                format: formatPattern,
            },
        };
    }
    else if (isPercentColumn(col)) {
        formatConfig = {
            ...formatConfig,
            category: CategoryType.Percentage,
            percentageFormatConfig: {
                removeTrailingZeroes: false,
                decimals: defaultBusinessFormatDecimalCount,
            },
        };
    }
    else if (isCurrencyColumn(col)) {
        let locale = null;
        if (getColumnCurrencyInfo(col).type === CurrencyFormatType.IsoCode) {
            locale = getColumnCurrencyInfo(col).isoCode;
        }
        else if (getUniqueCurrencyCode(col)) {
            locale = getUniqueCurrencyCode(col);
        }
        else {
            locale = enableInferCurrencyFromBrowserAtViz()
                ? blinkConstants.USER_LOCALE
                : getDefaultCurrencyCode();
        }
        formatConfig = {
            ...formatConfig,
            category: CategoryType.Currency,
            currencyFormatConfig: {
                toSeparateThousands: true,
                unit: isDefaultNumberFormatUnitNone() ? Unit.None : Unit.Auto,
                removeTrailingZeroes: false,
                locale,
                decimals: decimalCount,
            },
        };
    }
    else {
        formatConfig = {
            ...formatConfig,
            category: CategoryType.Number,
            numberFormatConfig: {
                toSeparateThousands: isEffectivelyNumeric(col),
                unit: isDefaultNumberFormatUnitNone() ||
                    !isEffectivelyNumeric(col)
                    ? Unit.None
                    : Unit.Auto,
                decimals: decimalCount,
                removeTrailingZeroes: false,
                negativeValueFormat: NegativeValueFormat.PrefixDash,
            },
        };
    }
    return formatConfig;
};
export const getColumnFormatPattern = (col) => {
    if (isColumnValidForNumberFormatting(col)) {
        return getNumericColumnFormatPattern(col);
    }
    return null;
};
export const getFormattingFromColumn = (column) => {
    if (!column || !isColumnValidForNumberFormatting(column)) {
        return null;
    }
    const userDefinedConfig = column.columnProps.columnProperties?.format;
    if (!_.isNil(userDefinedConfig)) {
        return userDefinedConfig;
    }
    return getColumnFormatPattern(column);
};
export const getFormattedValueForNumericColumn = (value, column) => {
    const formatPattern = getFormatPattern(column);
    const columnLocale = getLocale(column);
    const userDefinedFormatConfig = column.columnProps?.columnProperties?.format;
    if (isColumnValidForNumberFormatting(column) && typeof value === 'number') {
        if (userDefinedFormatConfig) {
            return getValueFormattedByConfig(value, userDefinedFormatConfig, {
                formatPattern,
                columnLocale,
            });
        }
        const formatConfig = getNumericColumnFormatPattern(column);
        return getValueFormattedByConfig(value, formatConfig, {
            formatPattern,
            columnLocale,
        });
    }
    if (Array.isArray(value) &&
        userDefinedFormatConfig &&
        userDefinedFormatConfig.category !== CategoryType.Custom) {
        return getValueFormattedByConfig(value[0], userDefinedFormatConfig, {
            formatPattern,
            columnLocale,
        });
    }
    return getDataFormatter(column)(value);
};
export const getFinalFormattedValue = (value, column, rowData, customFormat) => {
    const format = customFormat || getFormattingFromColumn(column);
    const currencyColumnId = _.get(column, 'format.currencyFormat.column');
    const formatPattern = getFormatPattern(column);
    const columnLocale = getLocale(column);
    let valuetoFormat = value;
    if (currencyColumnId && rowData) {
        valuetoFormat = [value, rowData[currencyColumnId]];
    }
    if (format && typeof value === 'number') {
        const formattedValueByConfig = getValueFormattedByConfig(valuetoFormat, format, { formatPattern, columnLocale });
        return formattedValueByConfig;
    }
    if (Array.isArray(valuetoFormat) &&
        format &&
        format.category !== CategoryType.Custom) {
        return getValueFormattedByConfig(valuetoFormat[0], format, {
            formatPattern,
            columnLocale,
        });
    }
    return getFormattedValue(column, valuetoFormat);
};
export const formatValueWithPrecision = (value, conditionalMetric, formatConfig) => {
    let decimalPrecision = formatConfig?.numberFormatConfig?.decimals ?? 0;
    const increaseDecimalPrecision = formatConfig?.numberFormatConfig?.increaseDecimalPrecision || 0;
    const metricDecimalCount = (conditionalMetric.value &&
        conditionalMetric.value.toString().split('.')[1]?.length) ??
        0;
    decimalPrecision = Math.max(decimalPrecision + increaseDecimalPrecision, metricDecimalCount);
    const formattedValue = new Intl.NumberFormat('en-US', {
        maximumFractionDigits: decimalPrecision,
        minimumFractionDigits: formatConfig?.numberFormatConfig
            ?.removeTrailingZeroes
            ? 0
            : decimalPrecision,
        useGrouping: false,
    }).format(value);
    if (_.isNaN(Number(formattedValue))) {
        return value;
    }
    return formattedValue;
};
const getDefaultFormattingForComparison = (formatConfig) => {
    const customFormatConfig = _.cloneDeep(formatConfig);
    if (_.isNil(customFormatConfig)) {
        return customFormatConfig;
    }
    customFormatConfig.numberFormatConfig = {
        ...(customFormatConfig.numberFormatConfig || {}),
        unit: Unit.None,
        negativeValueFormat: NegativeValueFormat.PrefixDash,
        toSeparateThousands: false,
        removeTrailingZeroes: true,
    };
    switch (customFormatConfig.category) {
        case CategoryType.Number:
            break;
        case CategoryType.Currency:
            customFormatConfig.numberFormatConfig.decimals =
                customFormatConfig.currencyFormatConfig?.decimals;
            customFormatConfig.category = CategoryType.Number;
            break;
        case CategoryType.Percentage:
            customFormatConfig.numberFormatConfig.decimals =
                customFormatConfig.percentageFormatConfig?.decimals;
            customFormatConfig.numberFormatConfig.increaseDecimalPrecision = 2;
            customFormatConfig.category = CategoryType.Number;
            break;
        case CategoryType.Custom:
            customFormatConfig.numberFormatConfig.decimals = decimalsForFormat(customFormatConfig, Unit.None);
            if (customFormatConfig.customFormatConfig.format?.endsWith('%')) {
                customFormatConfig.numberFormatConfig.increaseDecimalPrecision = 2;
            }
            customFormatConfig.category = CategoryType.Number;
            break;
        default:
    }
    return customFormatConfig;
};
export const getFormattedValueForComparison = (value, conditionalMetric, formatConfig, rowData) => {
    if (_.isNil(value)) {
        return { value, conditionalMetric };
    }
    const customFormat = getDefaultFormattingForComparison(formatConfig);
    let conditionalMetricCopy = conditionalMetric;
    switch (customFormat?.category) {
        case CategoryType.Number: {
            const formattedValue = formatValueWithPrecision(value, conditionalMetric, customFormat);
            if (conditionalMetric.value &&
                !Number.isNaN(Number(conditionalMetric.value))) {
                conditionalMetricCopy = _.cloneDeep(conditionalMetricCopy);
                conditionalMetricCopy.value = formatValueWithPrecision(Number(conditionalMetric.value), conditionalMetric, customFormat);
            }
            return {
                value: formattedValue,
                conditionalMetric: conditionalMetricCopy,
            };
        }
        default:
            return { value, conditionalMetric: conditionalMetricCopy };
    }
};
export function generateFontStyleCSS(style) {
    const { boldFontWeight, color, italicFontStyle, fontFamily, fontSize, underlineTextDecoration, } = style || {};
    let labelStyle = color ? `color:${color};` : '';
    labelStyle += fontFamily ? `font-family:${fontFamily};` : '';
    labelStyle += fontSize
        ? `font-size:${fontSize}${DEFAULT_HIGHCHARTS_CSS_UNIT};`
        : '';
    labelStyle += italicFontStyle ? 'font-style:italic;' : '';
    labelStyle += boldFontWeight ? 'font-weight:bold;' : '';
    labelStyle += underlineTextDecoration ? 'text-decoration:underline;' : '';
    return labelStyle;
}
//# sourceMappingURL=formatting-util.js.map